import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import CustomInput from "../../components/form/CustomInput";
import CustomSelect from "../../components/form/CustomSelect";
import CustomButton from "../../components/form/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    servicesOrderResource,
    suppliersResource,
    trailerTrucksResource,
    reportResource,
} from "../../helpers/configRoutes/backend_helper";
import { showFormErrors } from "../../helpers/showFormErrors";
import FileUploader from "../Loads/FileUpdater";
import PreviewPDF from "../../components/modal/PreviewPdf";
import { useSelector } from "react-redux";

const AddUpServiceOrder = ({ modal, setModal, setFilters, filters, onSubmitOverride }) => {
    const { validations, service } = useSelector(state => state.ServiceOrders);
    const [modalPrev, setModalPrev] = useState({ show: false, url: '' });
    const [loading, setLoading] = useState(false);

    // Estados de catálogos
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [trailers, setTrailers] = useState([]);
    const [serviceRequestOptions, setServiceRequestOptions] = useState([]);

    useEffect(() => {
        const getCatalogs = async () => {
            try {
                const response = await suppliersResource('get', { params: { select: true } });
                setSupplierOptions(response.suppliers || []);
                
                const responseTrucks = await trailerTrucksResource("get", { params: { select: true, truck: true } });
                setTrucks(responseTrucks.items || []);
                
                const responseTrailers = await trailerTrucksResource("get", { params: { select: true, truck: '' } });
                setTrailers(responseTrailers.items || []);

                const responseServiceRequests = await reportResource("get", {
                    params: {
                        select: true,
                        service_order_id: service.id || null,
                        perPage: 100,
                    }
                });
                setServiceRequestOptions(responseServiceRequests.reports || []);

            } catch (error) {
                console.error("Error obtaining catalogs:", error);
            }
        };

        getCatalogs();
    }, [service.id]);

    // Inicializamos Formik
    const validation = useFormik({
        initialValues: service,
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            ...validations
        }).test({
            name: "at-least-one",
            message: "Debes proporcionar al menos un truck_id o trailer_id",
            test: function (values) {
                return values.truck_id || values.trailer_id;
            }
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            if (onSubmitOverride) {
                const response = await onSubmitOverride(values, { setErrors });
                if (response?.status) {
                    setModal({ ...modal, show: false });
                } else if (response?.errors) {
                    setErrors(response.errors);
                }
                setLoading(false);
                return;
            }

            const response = await servicesOrderResource(values.id ? 'put' : 'post', values, values.id);
            if (response.status) {
                setFilters?.({ ...filters });
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors);
            }
            setLoading(false);
        }
    });

    useEffect(() => {
        if (!validation.values.supplier_type_id || validation.values.supplier_id || !supplierOptions.length) {
            return;
        }

        const matchedSuppliers = supplierOptions.filter(
            (supplier) => String(supplier.supplier_type_id) === String(validation.values.supplier_type_id)
        );

        if (matchedSuppliers.length === 1) {
            validation.setFieldValue('supplier_id', matchedSuppliers[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplierOptions, validation.values.supplier_type_id, validation.values.supplier_id]);

    useEffect(() => {
        const getSuggestedInternalId = async () => {
            if (service.id || validation.values.internalId) {
                return;
            }

            try {
                const response = await servicesOrderResource('get', {
                    params: {
                        suggested_internal_id: true,
                        service_request_id: validation.values.service_request_id || service.service_request_id || undefined,
                    }
                });

                if (response?.status && response?.internalId) {
                    validation.setFieldValue('internalId', response.internalId);
                }
            } catch (error) {
                console.error('Error obtaining suggested internal ID:', error);
            }
        };

        getSuggestedInternalId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service.id, validation.values.service_request_id]);

    const onChangeServiceRequest = (event) => {
        const value = event.target.value;
        validation.handleChange(event);

        const selectedRequest = serviceRequestOptions.find((item) => String(item.id) === String(value));
        validation.setFieldValue("service_request", selectedRequest || null);

        if (!selectedRequest) {
            return;
        }

        if (!validation.values.description) {
            validation.setFieldValue("description", selectedRequest.description || "");
        }

        if (!validation.values.truck_id && selectedRequest.truck?.id) {
            validation.setFieldValue("truck_id", selectedRequest.truck.id);
        }

        if (!validation.values.trailer_id && selectedRequest.trailer?.id) {
            validation.setFieldValue("trailer_id", selectedRequest.trailer.id);
        }
    };

    const deleteItem = (list, item) => {
        if (list === 'evidences') {
            const array = validation.values.evidences;
            const evidencesDeleted = validation.values.evidences_deleted || [];
            let newArray = array.filter((f) => f.id !== item.id);
            evidencesDeleted.push(item.id);
            validation.setFieldValue('evidences_deleted', evidencesDeleted);
            validation.setFieldValue('evidences', newArray);
        }
        if (list === 'stops') {
            const array = validation.values.stops;
            const stopsDeleted = validation.values.stops_deleted || [];
            let newArray = array.filter((f) => f.order !== item.order);
            stopsDeleted.push(item.id);
            validation.setFieldValue('stops_deleted', stopsDeleted);
            validation.setFieldValue('stops', newArray);
        }
    };

    // Efectos para kilometraje actual
    useEffect(() => {
        const getMilesTrailer = async () => {
            if (validation.values.trailer_id) {
                const responseTrailerMiles = await trailerTrucksResource('show', {}, validation.values.trailer_id);
                validation.setFieldValue('current_mileage_trailer', responseTrailerMiles.items?.current_mileage);
            }
        };
        getMilesTrailer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validation.values.trailer_id]);

    useEffect(() => {
        const getMilesTruck = async () => {
            if (validation.values.truck_id) {
                const responseTruckMiles = await trailerTrucksResource('show', {}, validation.values.truck_id);
                validation.setFieldValue('current_mileage_truck', responseTruckMiles.items?.current_mileage);
            }
        };
        getMilesTruck();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validation.values.truck_id]);

    // Componente visual para separar secciones
    const SectionDivider = ({ label }) => (
        <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-gray-200 flex-1"></div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{label}</span>
            <div className="h-px bg-gray-200 flex-1"></div>
        </div>
    );

    return (
        <BaseModalAnimated 
            modal={modal} 
            setModal={setModal} 
            size={'max-w-4xl'}
            title={validation.values.id ? "Edit Service Order" : "New Service Order"}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
                className="flex flex-col h-full"
            >
                {/* --- BODY SCROLLABLE --- 
                    Aquí agregué 'max-h-[70vh]' para limitar la altura y 'overflow-y-auto' para el scroll.
                */}
                <div className="p-6 overflow-y-auto custom-scrollbar bg-gray-50/50 max-h-[70vh]">
                    
                    {/* SECCIÓN 1: DATOS GENERALES */}
                    <div className="grid grid-cols-1 gap-4 mb-4">
                        <CustomSelect
                            label="Related service request"
                            name="service_request_id"
                            options={serviceRequestOptions}
                            holder="Select a service request"
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            error={validation?.errors?.service_request_id}
                            value={validation?.values?.service_request_id}
                            handleChange={onChangeServiceRequest}
                        />

                        {validation.values.service_request && (
                            <div className="rounded-xl border border-cs-border bg-white px-4 py-3 text-xs text-cs-muted">
                                <p className="font-semibold text-cs-dark">
                                    Request #{validation.values.service_request.id}
                                </p>
                                <p className="mt-1">{validation.values.service_request.description}</p>
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                                    <span className="rounded-full bg-cs-primary-soft px-2.5 py-1 text-cs-primary-dark">
                                        Operator: {validation.values.service_request.operator?.first_name} {validation.values.service_request.operator?.last_name}
                                    </span>
                                    {validation.values.service_request.truck?.number && (
                                        <span className="rounded-full bg-cs-bg-alt px-2.5 py-1 text-cs-dark">
                                            Truck: {validation.values.service_request.truck.number}
                                        </span>
                                    )}
                                    {validation.values.service_request.trailer?.number && (
                                        <span className="rounded-full bg-cs-bg-alt px-2.5 py-1 text-cs-dark">
                                            Trailer: {validation.values.service_request.trailer.number}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        <CustomInput
                            label="Service Description"
                            type="text"
                            name="description"
                            placeholder="e.g. Change of tires and oil check"
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            className="bg-white"
                            error={validation?.errors?.description}
                            value={validation?.values?.description}
                            handleChange={validation.handleChange}
                        />
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CustomInput
                                label="Internal ID"
                                type="text"
                                name="internalId"
                                placeholder="S-0000"
                                labelClassName="text-xs font-semibold text-gray-600 mb-1"
                                className="bg-white"
                                error={validation?.errors?.internalId}
                                value={validation?.values?.internalId}
                                handleChange={validation.handleChange}
                            />
                            <CustomInput
                                label="Creation Date"
                                type="date"
                                name="creation_date"
                                labelClassName="text-xs font-semibold text-gray-600 mb-1"
                                className="bg-white"
                                error={validation?.errors?.creation_date}
                                value={validation?.values?.creation_date}
                                handleChange={validation.handleChange}
                            />
                            <CustomInput
                                label="Due Date"
                                type="date"
                                name="due_date"
                                labelClassName="text-xs font-semibold text-gray-600 mb-1"
                                className="bg-white"
                                error={validation?.errors?.due_date}
                                value={validation?.values?.due_date}
                                handleChange={validation.handleChange}
                            />
                        </div>
                    </div>

                    <SectionDivider label="Provider & Costs" />

                    {/* SECCIÓN 2: PROVEEDOR */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <CustomSelect
                            label="Supplier"
                            name="supplier_id"
                            options={supplierOptions}
                            placeholder="Select Supplier..."
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            error={validation?.errors?.supplier_id}
                            value={validation?.values?.supplier_id}
                            handleChange={validation.handleChange}
                        />
                        <CustomInput
                            label="Estimated Cost"
                            type="text"
                            name="estimated_cost"
                            placeholder="$ 0.00"
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            className="bg-white"
                            error={validation?.errors?.estimated_cost}
                            value={validation?.values?.estimated_cost}
                            handleChange={validation.handleChange}
                        />
                    </div>

                    <SectionDivider label="Unit Assignment" />

                    {/* SECCIÓN 3: UNIDADES */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                        <CustomSelect
                            label="Truck Unit"
                            name="truck_id"
                            options={trucks}
                            placeholder="Select Truck..."
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            error={validation?.errors?.truck_id}
                            value={validation?.values?.truck_id}
                            handleChange={validation.handleChange}
                        />
                        <CustomSelect
                            label="Trailer Unit"
                            name="trailer_id"
                            options={trailers}
                            placeholder="Select Trailer..."
                            labelClassName="text-xs font-semibold text-gray-600 mb-1"
                            error={validation?.errors?.trailer_id}
                            value={validation?.values?.trailer_id}
                            handleChange={validation.handleChange}
                        />
                    </div>

                    {/* SECCIÓN 4: CONTROL DE KILOMETRAJE */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-5">
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-xs font-bold text-gray-700">Enable Mileage Control?</label>
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => validation.setFieldValue("mileage_control", 0)}
                                    className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${
                                        validation.values.mileage_control === 0 
                                        ? "bg-white text-gray-700 shadow-sm" 
                                        : "text-gray-400 hover:text-gray-600"
                                    }`}
                                >
                                    NO
                                </button>
                                <button
                                    type="button"
                                    onClick={() => validation.setFieldValue("mileage_control", 1)}
                                    className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${
                                        validation.values.mileage_control === 1 
                                        ? "bg-blue-500 text-white shadow-sm" 
                                        : "text-gray-400 hover:text-gray-600"
                                    }`}
                                >
                                    YES
                                </button>
                            </div>
                        </div>

                        {/* Campos condicionales de kilometraje */}
                        {validation.values.mileage_control === 1 && (
                            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-100 animate-fadeIn">
                                {/* Truck Miles */}
                                {validation.values.truck_id && (
                                    <div className="mb-4 last:mb-0">
                                        <h6 className="text-[10px] font-bold text-blue-800 uppercase mb-2">Truck Maintenance</h6>
                                        <div className="grid grid-cols-2 gap-3">
                                            <CustomInput
                                                label="Current Mileage"
                                                type="text"
                                                name="current_mileage_truck"
                                                labelClassName="text-[10px] font-semibold text-gray-500 mb-1"
                                                className="bg-white border-blue-200"
                                                error={validation?.errors?.current_mileage_truck}
                                                value={validation?.values?.current_mileage_truck}
                                                handleChange={validation.handleChange}
                                            />
                                            <CustomInput
                                                label="Service Interval (Miles)"
                                                type="text"
                                                name="miles_interval_truck"
                                                labelClassName="text-[10px] font-semibold text-gray-500 mb-1"
                                                className="bg-white border-blue-200"
                                                error={validation?.errors?.miles_interval_truck}
                                                value={validation?.values?.miles_interval_truck}
                                                handleChange={validation.handleChange}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Trailer Miles */}
                                {validation.values.trailer_id && (
                                    <div className="mt-3 pt-3 border-t border-blue-200/50">
                                        <h6 className="text-[10px] font-bold text-blue-800 uppercase mb-2">Trailer Maintenance</h6>
                                        <div className="grid grid-cols-2 gap-3">
                                            <CustomInput
                                                label="Current Mileage"
                                                type="text"
                                                name="current_mileage_trailer"
                                                labelClassName="text-[10px] font-semibold text-gray-500 mb-1"
                                                className="bg-white border-blue-200"
                                                error={validation?.errors?.current_mileage_trailer}
                                                value={validation?.values?.current_mileage_trailer}
                                                handleChange={validation.handleChange}
                                            />
                                            <CustomInput
                                                label="Service Interval (Miles)"
                                                type="text"
                                                name="miles_interval_trailer"
                                                labelClassName="text-[10px] font-semibold text-gray-500 mb-1"
                                                className="bg-white border-blue-200"
                                                error={validation?.errors?.miles_interval_trailer}
                                                value={validation?.values?.miles_interval_trailer}
                                                handleChange={validation.handleChange}
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {!validation.values.truck_id && !validation.values.trailer_id && (
                                    <p className="text-[10px] text-orange-500 italic text-center">
                                        Please select a Truck or Trailer above to configure mileage.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <SectionDivider label="Evidence & Documentation" />

                    <div className="mb-2">
                         <FileUploader validation={validation} setModalPrev={setModalPrev} deleteItem={deleteItem} />
                    </div>

                </div>

                {/* --- FOOTER --- */}
                <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-end gap-3 z-10">
                    <button
                        type="button"
                        onClick={() => setModal({ ...modal, show: false })}
                        className="
                            px-4 py-2 rounded-xl
                            text-xs font-medium
                            text-gray-500
                            hover:bg-gray-50
                            border border-transparent hover:border-gray-200
                            transition-all
                        "
                    >
                        Cancel
                    </button>
                    <CustomButton
                        loading={loading}
                        textLoad={'Saving...'}
                        type="submit"
                        className="
                            px-6 py-2 rounded-xl
                            bg-cs-primary hover:bg-cs-primary-hover
                            text-white text-xs font-bold
                            shadow-md hover:shadow-lg
                            transition-all
                        "
                    >
                        {validation.values.id ? "Save Changes" : "Create Service Order"}
                    </CustomButton>
                </div>
            </form>

            <PreviewPDF modal={modalPrev} setModal={setModalPrev} />
        </BaseModalAnimated>
    );
};

export default AddUpServiceOrder;